import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { apiRequest } from '../../api/client'
import { useAuth } from '../../auth/useAuth'
import { hasContainersModuleAccess } from '../../features/containers'
import { useAdaptivePolling } from '../../hooks/useAdaptivePolling'
import { errorStatus } from '../../pages/spot-search/api/spotSearchErrors'
import { ContainersOffersBadgeContext, type ContainersOffersBadgeValue } from './useContainersOffersBadge'

export const CONTAINERS_OFFERS_POLL_MS = 60_000

const getDelayMs = () => CONTAINERS_OFFERS_POLL_MS

// Licznik „nowe oferty” Skrzynki (spec §7, §14) na wzór SpotSearchAlertsProvider: jeden odczyt GET /api/containers/offers/count
// na całą aplikację zamiast hooka w Sidebarze, który montuje się na nowo przy każdej stronie (R39). Co 60 s przy widocznej
// karcie, powrót do karty najwyżej raz na 60 s, odczyt „po akcji” przez refresh() z kontekstu. Tylko dla firm z włączonym modułem.
// Bez importu z pages/containers (lazy chunk modułu zostaje poza głównym bundlem), więc ścieżka żyje tu i w containersApi:
// ContainersOffersBadgeProvider.test pilnuje, że to ten sam endpoint z tymi samymi opcjami co fetchOffersCount.
// 403/404 (moduł wyłączony w trakcie sesji albo backend bez Skrzynki) zatrzymuje odpytywanie do przeładowania.
export function ContainersOffersBadgeProvider({ children }: { children: ReactNode }) {
  const { token, profile } = useAuth()
  // Licznik związany z tożsamością: wylogowanie to clearAuth bez przeładowania, więc po zalogowaniu B menu nie może
  // pokazać licznika A (ani jego zatrzymania po 403) — stan należy do tokenu, który go odczytał.
  const [state, setState] = useState<{ token: string | null; count: number; stopped: boolean }>({
    token: null,
    count: 0,
    stopped: false,
  })
  const own = state.token === token
  const active = Boolean(token) && hasContainersModuleAccess(profile) && !(own && state.stopped)
  const abortRef = useRef<AbortController | null>(null)

  const tick = useCallback(async () => {
    if (!token) {
      return
    }
    const controller = new AbortController()
    abortRef.current = controller
    try {
      const response = await apiRequest<{ count?: unknown }>('/api/containers/offers/count', {
        token,
        signal: controller.signal,
        skipGlobalLoading: true,
        reportErrors: false,
      })
      if (controller.signal.aborted) {
        return
      }
      const count = typeof response?.count === 'number' && Number.isFinite(response.count) ? response.count : 0
      setState({ token, count, stopped: false })
    } catch (error) {
      if (controller.signal.aborted) {
        return
      }
      const status = errorStatus(error)
      if (status === 403 || status === 404) {
        setState({ token, count: 0, stopped: true })
      }
      // Inny błąd (sieć, 5xx): zostaje poprzednia wartość, następna próba za CONTAINERS_OFFERS_POLL_MS.
    }
  }, [token])

  const { requestImmediateRefresh } = useAdaptivePolling({
    enabled: active,
    getDelayMs,
    refresh: tick,
    minVisibilityRefreshGapMs: CONTAINERS_OFFERS_POLL_MS,
  })

  useEffect(() => {
    if (!active) {
      return undefined
    }
    // setTimeout(0) zamiast wywołania w ciele efektu — ten sam wzorzec co SpotSearchAlertsProvider (set-state-in-effect).
    const initialTimer = window.setTimeout(() => requestImmediateRefresh(), 0)
    return () => {
      window.clearTimeout(initialTimer)
      abortRef.current?.abort()
    }
  }, [active, requestImmediateRefresh, token])

  const value = useMemo<ContainersOffersBadgeValue>(
    () => ({ count: active && own ? state.count : 0, refresh: requestImmediateRefresh }),
    [active, own, requestImmediateRefresh, state.count]
  )

  return <ContainersOffersBadgeContext.Provider value={value}>{children}</ContainersOffersBadgeContext.Provider>
}
